import { buildCopyText } from '@copmer/calculator-integration/shared';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { useCalculatorCopyContext } from './copy-context';
import { useCalculatorCurrency } from './currency-context';
import Icon from './Icon';

export const CopyButton = ({ label = false }: { label?: boolean }) => {
    const ctx = useCalculatorCopyContext();
    const currency = useCalculatorCurrency();

    const isLoading = ctx?.isLoading;
    const copyOutputMonths = ctx?.copyOutputMonths ?? [];

    const [copyText, setCopyText] = useState<string | null>(null);

    const copyOutput = () => {
        try {
            // Built synchronously so the clipboard write stays within the click's user activation
            const text = buildCopyText({
                months: copyOutputMonths,
                selectedPeriod: ctx.activeMonth,
                currency,
            });

            setCopyText(text);
            void navigator.clipboard.writeText(text);
        } catch (error) {
            toast.error('Error copying output to clipboard', {
                description: error instanceof Error ? error.message : 'Something went wrong',
            });
        }
    };

    const triggerRef = useRef<HTMLButtonElement>(null);

    if (!ctx) {
        console.error('CopyButton must be used within a CalculatorCopyContextProvider');
        return null;
    }

    const disabled = isLoading || copyOutputMonths.length === 0;

    return (
        <TooltipProvider>
            <Tooltip delayDuration={0}>
                <TooltipTrigger onClick={(event) => event.preventDefault()} ref={triggerRef}>
                    <Button
                        type="button"
                        variant="icon"
                        size={label ? 'iconText' : 'icon'}
                        disabled={disabled}
                        onClick={copyOutput}
                    >
                        <Icon icon="Copy" className="h-6 w-6" />
                        {label ? <span className="ml-2">Copy</span> : null}
                    </Button>
                </TooltipTrigger>
                <TooltipContent
                    onPointerDownOutside={(event) => {
                        if (triggerRef.current?.contains(event.target as any)) event.preventDefault();
                    }}
                >
                    <p className="whitespace-pre">{copyText ? copyText : 'Copy Calculator Output'}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
